'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { Menu, X } from 'lucide-react';

type TimelineEntry = {
  year: string;
  title: string;
  description: string;
  image: string;
  alt: string;
};

const timeline: TimelineEntry[] = [
  {
    year: '1920s',
    title: 'The Spark of an Idea',
    description:
      "In a small garage, our founder, Elias Vance, begins experimenting with radio technology, laying the groundwork for what's to come.",
    image: 'https://picsum.photos/seed/journey1/800/600',
    alt: 'Black and white photo of a vintage workshop',
  },
  {
    year: '1950s',
    title: 'The Mainframe Era',
    description:
      "We introduce the 'Odyssey I', one of the first commercially available computers, revolutionizing data processing for large enterprises.",
    image: 'https://picsum.photos/seed/journey2/800/600',
    alt: 'Photo of an early computer prototype from the 1950s',
  },
  {
    year: '1980s',
    title: 'The Personal Revolution',
    description:
      "With the launch of the 'Companion', we bring the power of computing into homes and small businesses across the nation.",
    image: 'https://picsum.photos/seed/journey3/800/600',
    alt: 'A retro 1980s personal computer on a desk',
  },
  {
    year: 'Today',
    title: 'Connecting the World',
    description:
      'Our journey continues as we build the next generation of global communication networks and AI-powered tools.',
    image: 'https://picsum.photos/seed/journey4/800/600',
    alt: 'Modern diverse team collaborating around a glowing interface',
  },
];

function TimelineItem({ entry, isReversed }: { entry: TimelineEntry; isReversed: boolean }) {
  return (
    <li className="relative flex flex-col md:grid md:grid-cols-2 md:gap-12 items-center">
      {/* Dot */}
      <span className="absolute top-4 md:top-1/2 left-0 md:left-1/2 md:-translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white border-4 border-gray-300 rounded-full flex items-center justify-center z-10">
        <span className="w-3 h-3 bg-indigo-600 rounded-full"></span>
      </span>

      {/* Text */}
      <div
        className={`relative w-full md:w-auto pl-12 md:pl-0 
          ${isReversed ? 'md:order-2 md:pl-12 md:text-left' : 'md:order-1 md:pr-12 md:text-right'}
        `}
      >
        <p className="text-indigo-600 font-semibold mb-1">{entry.year}</p>
        <h3 className="text-3xl font-bold">{entry.title}</h3>
        <p className="mt-2 text-gray-600">{entry.description}</p>
      </div>

      {/* Image */}
      <div
        className={`relative w-full md:w-auto pl-12 md:pl-0 
          ${isReversed ? 'md:order-1' : 'md:order-2'}
        `}
      >
        <Image
          src={entry.image}
          alt={entry.alt}
          width={800}
          height={600}
          className="rounded-lg shadow-md"
        />
      </div>
    </li>
  );
}


export function TimelineJourneyHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-gray-100 text-gray-800 font-sans">
      {/* Header */}
      <header className="p-6 z-50 flex justify-between items-center bg-white/80 backdrop-blur-sm sticky top-0 z-10 border-b">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Our Odyssey</h1>
        <nav className="hidden md:block">
          <Button variant="outline">The Beginning</Button>
        </nav>
        <div className="md:hidden">
          <Button
            size="icon"
            variant="ghost"
            aria-label="Toggle Menu"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white/95 border-b">
          <nav className="container mx-auto flex flex-col items-center p-6">
            <Button variant="outline" className="w-full">
              The Beginning
            </Button>
          </nav>
        </div>
      )}

      {/* Main */}
      <main className="container mx-auto px-6 py-20">
        {/* Hero */}
        <section className="text-center max-w-3xl mx-auto">
          <h2 className="text-5xl md:text-6xl font-bold tracking-tighter">A Century of Innovation</h2>
          <p className="mt-4 text-lg text-gray-600">
            From a humble workshop to a global leader, follow the journey of how we shaped the
            future, one decade at a time.
          </p>
        </section>

        {/* Timeline */}
        <section className="mt-24 relative">
          {/* Vertical Line */}
          <div className="absolute top-0 left-4 md:left-1/2 md:-translate-x-1/2 h-full w-0.5 bg-gray-300"></div>

          <ol className="space-y-24">
            {timeline.map((entry, idx) => (
              <TimelineItem key={entry.year} entry={entry} isReversed={idx % 2 === 1} />
            ))}
          </ol>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-16 mt-20 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold">Our Future is Unwritten.</h2>
          <p className="mt-2 text-gray-300 max-w-xl mx-auto">
            We're always looking for the next generation of innovators to join us on our journey.
          </p>
          <Button className="mt-8 bg-indigo-500 hover:bg-indigo-600">Explore Careers</Button>
          <div className="mt-12 text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} The Odyssey Corporation.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
